import { Collapse } from '@mui/material'
import {
  AccountPerformanceOnDay,
  ApiAssociatedAccount,
  ApiPortfolioType,
  useGetByTenantAccountQuery
} from 'api/rtk-api'
import clsx from 'clsx'
import { Card, RotatingChevronIcon, Rule, Tabs, TradeStatusAlert } from 'components/core-v2'
import { useState } from 'react'
import { formatCurrencyFull } from 'utilities/currency'
import { useUser } from 'utilities/hooks/useUser'

import { CURRENCY, TENANT } from '@/const'

import { isUserInRoles } from '../../../tradebuilder-v2/util/user'
import classes from './index.module.scss'
import { TradeOverview } from './trade-overview'
import { TransferSwitch } from './transfer-switch'

interface ExtendedTrade extends ApiAssociatedAccount {
  accountSource: string
  last30DaysPeformancePercent: number
  last30DaysPerformance: AccountPerformanceOnDay[]
  portfolioType: ApiPortfolioType
  strategyTemplateName: string
}
interface TradeProps {
  businessUnitId: string
  trade: ExtendedTrade
  tradeIds: { label: string; value: string }[]
}

enum TradeTabs {
  Overview = 'Overview',
  TransferSwitch = 'TransferSwitch'
}

const Trade = ({ businessUnitId, trade, tradeIds }: TradeProps) => {
  const { data: user } = useUser()
  const isSwitchAllowed = isUserInRoles(user, ['Editor', 'Sales', 'BankAdmin', 'Administrator'])
  const [isOpen, setIsOpen] = useState(false)
  const [tab, setTab] = useState(TradeTabs.Overview)

  return (
    <Card.Wrapper
      className={clsx(classes.trade, {
        [classes.shadow]: isOpen
      })}
      spacing="xs"
    >
      <button type="button" onClick={() => setIsOpen(!isOpen)}>
        <span className={classes.accountName}>
          <RotatingChevronIcon isRotated={isOpen} />
          {trade.accountName}
        </span>
        <span className={classes.accountStatus}>{trade.accountStatus}</span>
        <span className={classes.accountValue}>
          {formatCurrencyFull(trade.accountValue, CURRENCY)}
        </span>
      </button>
      <Collapse in={isOpen}>
        <Rule />
        <TradeStatusAlert
          className="mt-4"
          tradeId={trade.accountId}
          tradeSource={trade.accountSource}
        />
        <Tabs.List className="pt-4" spacing="sm">
          <Tabs.Tab
            isSelected={tab === TradeTabs.Overview}
            value={TradeTabs.Overview}
            onClick={() => setTab(TradeTabs.Overview)}
          >
            Overview
          </Tabs.Tab>
          {isSwitchAllowed && (
            <Tabs.Tab
              isSelected={tab === TradeTabs.TransferSwitch}
              value={TradeTabs.TransferSwitch}
              onClick={() => setTab(TradeTabs.TransferSwitch)}
            >
              Trade switch
            </Tabs.Tab>
          )}
        </Tabs.List>
        {tab === TradeTabs.Overview && (
          <Tabs.Panel value={TradeTabs.Overview}>
            <TradeOverview trade={trade} />
          </Tabs.Panel>
        )}
        {tab === TradeTabs.TransferSwitch && (
          <Tabs.Panel value={TradeTabs.TransferSwitch}>
            <TransferSwitch businessUnitId={businessUnitId} trade={trade} tradeIds={tradeIds} />
          </Tabs.Panel>
        )}
      </Collapse>
    </Card.Wrapper>
  )
}

interface Props {
  businessUnitId: string
  noteId: string
  trades: ExtendedTrade[]
}

export const TradesTab = ({ businessUnitId, noteId, trades }: Props) => {
  const { data } = useGetByTenantAccountQuery({
    tenant: TENANT,
    noteIds: noteId
  })

  const tradeIds = trades
    .filter(({ accountId, accountStatus }) => {
      if (!data) return false
      const account = data.accounts.find(account => account.accountId === accountId)
      return (
        (accountStatus === 'Running' || accountStatus === 'New') &&
        account &&
        !(account.currentlyApproving || account.currentlyClosing || account.currentlyUpdating) &&
        !account.isLockedForUpdates
      )
    })
    .map(({ accountId, accountName }) => ({
      label: accountName,
      value: accountId
    }))
  return (
    <>
      <div className={classes.headingRow}>
        <span>Name</span>
        <span>Status</span>
        <span>Current value</span>
      </div>
      <ul>
        {trades.map(trade => (
          <Trade
            key={trade.accountId}
            businessUnitId={businessUnitId}
            trade={trade}
            tradeIds={tradeIds}
          />
        ))}
      </ul>
    </>
  )
}
